import React, { useMemo, useState } from 'react';
import { Service } from '../types/booking.types';
import ServiceItem from './ServiceItem';

interface ServicesTabProps {
  services: Service[];
  additionalServices?: Service[];
  onAddService: () => void;
  onCancelService?: (
    serviceOrServices: Service | Service[],
    meta?: {
      isFullCancellation?: boolean;
      selectedCount?: number;
      totalCancellableCount?: number;
    }
  ) => void;
}

const ServicesTab: React.FC<ServicesTabProps> = ({ services, additionalServices = [], onAddService, onCancelService }) => {
  const normalizedAdditional = useMemo(
    () =>
      additionalServices.map((service) => ({
        ...service,
        is_additional: service.is_additional ?? true,
      })),
    [additionalServices]
  );

  const allServices = useMemo(() => [...services, ...normalizedAdditional], [services, normalizedAdditional]);

  const getServiceKey = (service: Service, index: number) => `service-${service.id}-${index}`;

  const allCheckboxIds = useMemo(() => {
    return allServices.map((service, serviceIndex) => getServiceKey(service, serviceIndex));
  }, [allServices]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  const handleSelectAllChange = (checked: boolean) => {
    setIsSelectAllChecked(checked);
    setSelectedIds(checked ? new Set(allCheckboxIds) : new Set());
  };

  const handleIndividualChange = (id: string, checked: boolean) => {
    setIsSelectAllChecked(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const selectedCancellableServices = useMemo(() => {
    return allServices.filter((service, index) => {
      const key = getServiceKey(service, index);
      const isSelected = selectedIds.has(key);
      const isCancellable = service.status !== 'cancelled' && service.status !== 'completed';
      return isSelected && isCancellable;
    });
  }, [allServices, selectedIds]);
  const cancellableServicesCount = useMemo(
    () => allServices.filter((service) => service.status !== 'cancelled' && service.status !== 'completed').length,
    [allServices]
  );

  const canUseCancelButton = Boolean(onCancelService && selectedCancellableServices.length > 0);

  return (
    <div className="mb-6">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <h3 className="text-lg font-bold text-gray-800 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          Services Ordered
        </h3>

        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isSelectAllChecked}
              onChange={(e) => handleSelectAllChange(e.target.checked)}
              className="h-4 w-4"
            />
            <span>Select All</span>
          </label>

          <button
            type="button"
            onClick={() => {
              if (canUseCancelButton) {
                onCancelService?.(selectedCancellableServices, {
                  isFullCancellation:
                    cancellableServicesCount > 0 &&
                    selectedCancellableServices.length === cancellableServicesCount,
                  selectedCount: selectedCancellableServices.length,
                  totalCancellableCount: cancellableServicesCount,
                });
              }
            }}
            disabled={!canUseCancelButton}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow transition-all hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg shadow hover:bg-indigo-700 transition-all text-sm font-medium flex items-center space-x-2"
            onClick={onAddService}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Add Additional Service</span>
          </button>
        </div>
      </div>

      {allServices.length > 0 ? (
        allServices.map((service, index) => {
          const serviceKey = getServiceKey(service, index);

          return (
            <ServiceItem
              key={`${service.id}-${index}`}
              service={service}
              index={index}
              onCancelService={(serviceOrServices) => {
                const selectedCount = Array.isArray(serviceOrServices) ? serviceOrServices.length : 1;
                onCancelService?.(serviceOrServices, {
                  isFullCancellation: cancellableServicesCount > 0 && selectedCount === cancellableServicesCount,
                  selectedCount,
                  totalCancellableCount: cancellableServicesCount,
                });
              }}
              serviceKey={serviceKey}
              checkedService={selectedIds.has(serviceKey)}
              onServiceCheckChange={(checked) => handleIndividualChange(serviceKey, checked)}
            />
          );
        })
      ) : (
        <p className="py-8 text-center text-gray-500">No services found.</p>
      )}

      <div className="mt-6 rounded border-l-4 border-blue-500 bg-blue-50 p-4">
        <div className="flex items-start">
          <svg className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <h4 className="mb-1 text-sm font-semibold text-blue-800">About Additional Services</h4>
            <p className="text-sm text-blue-700">
              Additional services can be added to this booking after the initial order. These services will be marked with an{' '}
              <span className="mx-1 inline-flex items-center rounded bg-orange-500 px-2 py-0.5 text-xs font-semibold text-white">
                Additional Service
              </span>{' '}
              badge and may affect the final booking amount.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesTab;
